import { INovaEntity } from './base';
import { isCarrierUser, isInternalUser, isWarehouseUser, type IUser } from './user';

/**
 * Per-country identifier rules. Array order is significant: it is the most-to-least-important
 * display priority for that country's identifiers.
 * {@link COMPANY_IDENTIFIER_TYPES} is derived from these rules.
 */
export const COMPANY_IDENTIFIER_RULES_BY_COUNTRY = {
  // North America
  USA: [
    { identifierType: 'usdot', required: false },
    { identifierType: 'mc', required: false },
    { identifierType: 'scac', required: false }
  ],
  CAN: [
    { identifierType: 'nsc', required: false },
    { identifierType: 'cvor', required: false }
  ],
  MEX: [{ identifierType: 'permiso_sict', required: false }],
  PRI: [
    { identifierType: 'usdot', required: false },
    { identifierType: 'mc', required: false }
  ],
  // LATAM
  BRA: [
    { identifierType: 'cnpj', required: false },
    { identifierType: 'rntrc', required: false }
  ],
  ARG: [{ identifierType: 'ruta', required: false }],
  COL: [
    { identifierType: 'habilitacion_mt', required: false },
    { identifierType: 'nit', required: false }
  ],
  CHL: [{ identifierType: 'rut', required: false }],
  PER: [{ identifierType: 'mtc', required: false }],
  ECU: [{ identifierType: 'ant', required: false }],
  URY: [{ identifierType: 'mtop', required: false }],
  PRY: [{ identifierType: 'mopc', required: false }],
  VEN: [{ identifierType: 'rnt', required: false }],
  CRI: [{ identifierType: 'mopt', required: false }],
  GTM: [{ identifierType: 'dgtt', required: false }],
  HND: [{ identifierType: 'dgst', required: false }],
  NIC: [{ identifierType: 'mti', required: false }],
  PAN: [{ identifierType: 'attt', required: false }],
  SLV: [{ identifierType: 'vmt', required: false }],
  // Caribbean
  DOM: [{ identifierType: 'intrant', required: false }],
  JAM: [{ identifierType: 'ta', required: false }],
  TTO: [{ identifierType: 'mowt', required: false }],
  HTI: [{ identifierType: 'mtptc', required: false }],
  BLZ: [{ identifierType: 'dot_bz', required: false }],
  GUY: [{ identifierType: 'gta', required: false }],
  // Europe
  GBR: [{ identifierType: 'o_licence', required: false }],
  DEU: [{ identifierType: 'vat', required: false }],
  FRA: [{ identifierType: 'vat', required: false }],
  ESP: [
    { identifierType: 'vat', required: false },
    { identifierType: 'auth_number', required: false }
  ],
  ITA: [
    { identifierType: 'vat', required: false },
    { identifierType: 'albo', required: false }
  ],
  NLD: [{ identifierType: 'vat', required: false }],
  BEL: [{ identifierType: 'vat', required: false }],
  SWE: [{ identifierType: 'vat', required: false }],
  POL: [{ identifierType: 'vat', required: false }],
  PRT: [{ identifierType: 'vat', required: false }],
  IRL: [
    { identifierType: 'rhol', required: false },
    { identifierType: 'vat', required: false }
  ],
  HUN: [{ identifierType: 'vat', required: false }],
  MLT: [{ identifierType: 'vat', required: false }],
  CHE: [{ identifierType: 'road_haulage_authorisation', required: false }],
  // APAC
  AUS: [{ identifierType: 'abn', required: false }],
  NZL: [
    { identifierType: 'tsl', required: false },
    { identifierType: 'nzbn', required: false }
  ],
  JPN: [{ identifierType: 'mlit_no', required: false }],
  KOR: [{ identifierType: 'molit', required: false }],
  MYS: [{ identifierType: 'gvl', required: false }],
  PHL: [{ identifierType: 'ltfrb', required: false }],
  SGP: [{ identifierType: 'uen', required: false }],
  IND: [{ identifierType: 'rto', required: false }],
  // Middle East
  SAU: [{ identifierType: 'tga', required: false }],
  // Other
  TUR: [{ identifierType: 'u_yetki', required: false }]
} as const;

type CompanyIdentifierRulesByCountry = typeof COMPANY_IDENTIFIER_RULES_BY_COUNTRY;

type CarrierCompanyIdentifierType =
  CompanyIdentifierRulesByCountry[keyof CompanyIdentifierRulesByCountry][number]['identifierType'];

/** Identifier types that aren't tied to a specific country's rules. */
const GLOBAL_COMPANY_IDENTIFIER_TYPES = ['email_domain', 'carrier_id'] as const;

export type CompanyIdentifierType =
  | CarrierCompanyIdentifierType
  | (typeof GLOBAL_COMPANY_IDENTIFIER_TYPES)[number];

/**
 * Known identifier types; new values may be added without schema changes.
 *
 * Order is significant: it is the global most-to-least-important priority used to rank a
 * company's identifiers for display when its country is missing or has no rules of its own.
 */
export const COMPANY_IDENTIFIER_TYPES: readonly CompanyIdentifierType[] = [
  ...new Set<CompanyIdentifierType>(
    Object.values(COMPANY_IDENTIFIER_RULES_BY_COUNTRY).flatMap(rules =>
      rules.map(rule => rule.identifierType as CompanyIdentifierType)
    )
  ),
  ...GLOBAL_COMPANY_IDENTIFIER_TYPES
];

export interface CompanyIdentifierCountryRule {
  identifierType: CompanyIdentifierType;
  required: boolean;
  /** Optional hint text for the UI, e.g. shown under the identifier's input field. */
  description?: string;
}

export type CompanyIdentifierCountryCode = keyof typeof COMPANY_IDENTIFIER_RULES_BY_COUNTRY;

export const COMPANY_IDENTIFIER_COUNTRIES = Object.keys(
  COMPANY_IDENTIFIER_RULES_BY_COUNTRY
) as CompanyIdentifierCountryCode[];

const EMPTY_COMPANY_IDENTIFIER_COUNTRY_RULES: readonly CompanyIdentifierCountryRule[] = [];

export function isSupportedCountry(
  countryCode: string | null | undefined
): countryCode is CompanyIdentifierCountryCode {
  if (!countryCode) {
    return false;
  }

  return Object.hasOwn(COMPANY_IDENTIFIER_RULES_BY_COUNTRY, countryCode);
}

export function getCompanyIdentifierRulesByCountry(
  countryCode: string | null | undefined
): readonly CompanyIdentifierCountryRule[] {
  if (!isSupportedCountry(countryCode)) {
    return EMPTY_COMPANY_IDENTIFIER_COUNTRY_RULES;
  }

  return COMPANY_IDENTIFIER_RULES_BY_COUNTRY[countryCode];
}

export function getCompanyIdentifierTypesByCountry(
  countryCode: string | null | undefined
): CompanyIdentifierType[] {
  return getCompanyIdentifierRulesByCountry(countryCode).map(
    ({ identifierType }) => identifierType
  );
}

export function getRequiredCompanyIdentifierTypesByCountry(
  countryCode: string | null | undefined
): CompanyIdentifierType[] {
  return getCompanyIdentifierRulesByCountry(countryCode)
    .filter(({ required }) => required)
    .map(({ identifierType }) => identifierType);
}

export function isCompanyIdentifierTypeValidForCountry(
  countryCode: string | null | undefined,
  identifierType: CompanyIdentifierType
): boolean {
  return getCompanyIdentifierRulesByCountry(countryCode).some(
    companyIdentifierCountryRule => companyIdentifierCountryRule.identifierType === identifierType
  );
}

export const COMPANY_IDENTIFIER_SOURCES = [
  'warehouse',
  'self_reported',
  'internal_user',
  'highway_lookup'
] as const;

export type CompanyIdentifierSource = (typeof COMPANY_IDENTIFIER_SOURCES)[number];

export const COMPANY_IDENTIFIER_VERIFIED_BY = ['internal_user', 'highway'] as const;

export type CompanyIdentifierVerifiedBy = (typeof COMPANY_IDENTIFIER_VERIFIED_BY)[number];

export interface ICompanyIdentifier extends INovaEntity {
  companyId: string;
  identifierType: CompanyIdentifierType;
  identifierValue: string;
  source?: CompanyIdentifierSource | null;
  verifiedBy?: CompanyIdentifierVerifiedBy | null;
  verifiedAt?: Date | null;
}

export function getSourceByUser(user: IUser): CompanyIdentifierSource {
  if (isInternalUser(user)) {
    return 'internal_user';
  }

  if (isWarehouseUser(user)) {
    return 'warehouse';
  }

  if (isCarrierUser(user)) {
    return 'self_reported';
  }

  return 'self_reported';
}
